/**
 * 消息子类型枚举
 */
export enum MessageSubType {
  /** 职位发布 */
  JOB_PUBLISH = 1,
  /** 职位开放 */
  JOB_OPEN = 2,
  /** 职位关闭 */
  JOB_CLOSE = 3,
  /** 新投递 */
  NEW_APPLICATION = 10,
  /** 选考状态变更 */
  SELECTION_STATUS_CHANGE = 11,
  /** 面试日程已发出 面试日程待确认*/
  INTERVIEW_SCHEDULE_SENT = 12,
  /** 面试日程已接受 */
  INTERVIEW_SCHEDULE_ACCEPTED = 13,
  /** 面试日程待调整 */
  INTERVIEW_SCHEDULE_PENDING = 14,
  /** 面试日程已调整 */
  INTERVIEW_SCHEDULE_ADJUSTED = 15,
  /** 面试日程已过期 */
  INTERVIEW_SCHEDULE_EXPIRED = 16,
  /** 面试日程重新预约 */
  INTERVIEW_SCHEDULE_RESCHEDULED = 17,
  /** 入社予定日調整 */
  EXPECTED_JOIN_DATE_ADJUSTED = 18,
  /** 确认入职 */
  CONFIRM_JOIN = 19,
  /** 选考终止 */
  SELECTION_TERMINATE = 20,
  /** 内部留言 */
  INTERNAL_COMMENT = 21,
  /** 外部联络 */
  EXTERNAL_CONTACT = 22,
  /** 内定通知已发放 */
  OFFER_LETTER_SENT = 24,
  /** 候补者注册（新增类型，实际枚举值待后端确认） */
  CANDIDATE_REGISTER = 23
}

/**
 * 消息内容由一串有序的片段（segment）拼接而成，每种片段描述一种渲染方式。
 * 新增消息类型或调整文案结构时，只需要在下面的 MessageRenderConfigs 里增删片段即可，
 * 不需要改动 message-formatter.ts 的渲染逻辑。
 */
export interface MessageTemplateSegment {
  kind: 'template'
  /** i18n key，模板内使用 [[field]] 占位符 */
  templateKey: string
  /** 模板中需要加粗展示的占位符字段 */
  boldFields?: string[]
  /** 占位符字段的兜底值，如留言内容为空时展示为 "-" */
  fieldFallbacks?: Record<string, string>
}

export interface MessageTextSegment {
  kind: 'text'
  /** 纯文本 i18n key */
  i18nKey: string
}

export interface MessageBoldTextSegment {
  kind: 'boldText'
  /** 加粗文本 i18n key */
  i18nKey: string
}

export interface MessageBreakSegment {
  kind: 'break'
}

export interface MessageLabelValueSegment {
  kind: 'labelValue'
  /** 标签文案 i18n key，如“候选人来源：” */
  labelKey: string
  /** 取值字段名（来自消息 contentData） */
  field: string
  /** 字段为空时的兜底文案 */
  fallback?: string
}

export type MessageSegment =
  | MessageTemplateSegment
  | MessageTextSegment
  | MessageBoldTextSegment
  | MessageBreakSegment
  | MessageLabelValueSegment

/**
 * 单一端（企业端 / 猎头端）的消息渲染配置：按顺序渲染的片段列表
 */
export interface MessageSideConfig {
  segments: MessageSegment[]
}

/**
 * 某一消息子类型的完整渲染配置
 */
export interface MessageRenderConfig {
  /** 企业端渲染配置 */
  biz: MessageSideConfig
  /** 猎头端渲染配置，不存在时复用企业端配置 */
  agent?: MessageSideConfig
}

/** 尾行：候选人来源（企业端） */
const FOOTER_SOURCE: MessageSegment[] = [
  { kind: 'break' },
  { kind: 'labelValue', labelKey: 'notify.messageTemplates.candidateSource', field: 'sourceField' }
]
/** 尾行：招聘企业（猎头端） */
const FOOTER_ENTERPRISE: MessageSegment[] = [
  { kind: 'break' },
  { kind: 'labelValue', labelKey: 'notify.messageTemplates.enterpriseName', field: 'enterpriseName' }
]

/**
 * 消息子类型 -> 渲染配置的对应关系
 * 新增/调整消息类型时，只需要在这里维护一份即可：模板 key、需要加粗的字段、
 * 以及模板之后追加的固定文案/尾行，都在此处集中描述，不用再去 message-formatter.ts 里找。
 */
export const MessageRenderConfigs: Record<MessageSubType, MessageRenderConfig> = {
  [MessageSubType.JOB_PUBLISH]: {
    biz: {
      segments: [{ kind: 'template', templateKey: 'notify.messageTemplates.jobPublish', boldFields: ['userName', 'jobTitle'] }]
    }
  },
  [MessageSubType.JOB_OPEN]: {
    biz: {
      segments: [{ kind: 'template', templateKey: 'notify.messageTemplates.jobOpen', boldFields: ['jobTitle', 'headhunterCompanyNames'] }]
    },
    agent: {
      segments: [{ kind: 'template', templateKey: 'notify.messageTemplates.agent.jobOpen', boldFields: ['enterpriseName', 'jobTitle'] }]
    }
  },
  [MessageSubType.JOB_CLOSE]: {
    biz: {
      segments: [{ kind: 'template', templateKey: 'notify.messageTemplates.jobClose', boldFields: ['userName', 'jobTitle'] }]
    },
    agent: {
      segments: [{ kind: 'template', templateKey: 'notify.messageTemplates.agent.jobClose', boldFields: ['enterpriseName', 'jobTitle'] }]
    }
  },
  [MessageSubType.NEW_APPLICATION]: {
    biz: {
      segments: [
        { kind: 'template', templateKey: 'notify.messageTemplates.newApplication', boldFields: ['jobTitle'] },
        ...FOOTER_SOURCE
      ]
    },
    agent: {
      segments: [
        { kind: 'template', templateKey: 'notify.messageTemplates.agent.newApplication', boldFields: ['candidateName', 'jobTitle'] },
        ...FOOTER_ENTERPRISE
      ]
    }
  },
  [MessageSubType.SELECTION_STATUS_CHANGE]: {
    biz: {
      segments: [
        { kind: 'template', templateKey: 'notify.messageTemplates.selectionStatusChange', boldFields: ['candidateName', 'stepName'] },
        ...FOOTER_SOURCE
      ]
    },
    agent: {
      segments: [
        { kind: 'template', templateKey: 'notify.messageTemplates.agent.selectionStatusChange', boldFields: ['candidateName', 'stepName'] },
        ...FOOTER_ENTERPRISE
      ]
    }
  },
  [MessageSubType.INTERVIEW_SCHEDULE_SENT]: {
    biz: {
      segments: [
        { kind: 'template', templateKey: 'notify.messageTemplates.interviewScheduleSent', boldFields: ['candidateName'] },
        ...FOOTER_SOURCE
      ]
    },
    agent: {
      segments: [
        { kind: 'template', templateKey: 'notify.messageTemplates.agent.interviewScheduleSent', boldFields: ['candidateName'] },
        ...FOOTER_ENTERPRISE
      ]
    }
  },
  [MessageSubType.INTERVIEW_SCHEDULE_ACCEPTED]: {
    biz: {
      segments: [
        { kind: 'template', templateKey: 'notify.messageTemplates.interviewScheduleAccepted', boldFields: ['candidateName', 'interviewTime'] },
        ...FOOTER_SOURCE
      ]
    },
    agent: {
      segments: [
        {
          kind: 'template',
          templateKey: 'notify.messageTemplates.agent.interviewScheduleAccepted',
          boldFields: ['candidateName', 'interviewTime']
        },
        ...FOOTER_ENTERPRISE
      ]
    }
  },
  [MessageSubType.INTERVIEW_SCHEDULE_PENDING]: {
    biz: {
      segments: [
        { kind: 'template', templateKey: 'notify.messageTemplates.interviewSchedulePending', boldFields: ['candidateName'] },
        { kind: 'break' },
        { kind: 'labelValue', labelKey: 'notify.messageTemplates.remark', field: 'remarkContent', fallback: '-' },
        ...FOOTER_SOURCE
      ]
    },
    agent: {
      segments: [
        { kind: 'template', templateKey: 'notify.messageTemplates.agent.interviewSchedulePending', boldFields: ['candidateName'] },
        { kind: 'break' },
        { kind: 'labelValue', labelKey: 'notify.messageTemplates.remark', field: 'remarkContent', fallback: '-' },
        ...FOOTER_ENTERPRISE
      ]
    }
  },
  [MessageSubType.INTERVIEW_SCHEDULE_ADJUSTED]: {
    biz: {
      segments: [
        { kind: 'template', templateKey: 'notify.messageTemplates.interviewScheduleAdjusted', boldFields: ['candidateName'] },
        { kind: 'boldText', i18nKey: 'notify.messageTemplates.adjusted' },
        ...FOOTER_SOURCE
      ]
    },
    agent: {
      segments: [
        { kind: 'template', templateKey: 'notify.messageTemplates.agent.interviewScheduleAdjusted', boldFields: ['candidateName'] },
        { kind: 'boldText', i18nKey: 'notify.messageTemplates.adjusted' },
        ...FOOTER_ENTERPRISE
      ]
    }
  },
  [MessageSubType.INTERVIEW_SCHEDULE_EXPIRED]: {
    biz: {
      segments: [
        { kind: 'template', templateKey: 'notify.messageTemplates.interviewScheduleExpired', boldFields: ['candidateName'] },
        { kind: 'boldText', i18nKey: 'notify.messageTemplates.expired' },
        { kind: 'text', i18nKey: 'notify.messageTemplates.awaitingReschedule' },
        ...FOOTER_SOURCE
      ]
    },
    agent: {
      segments: [
        { kind: 'template', templateKey: 'notify.messageTemplates.agent.interviewScheduleExpired', boldFields: ['candidateName'] },
        { kind: 'boldText', i18nKey: 'notify.messageTemplates.expired' },
        { kind: 'text', i18nKey: 'notify.messageTemplates.awaitingReschedule' },
        ...FOOTER_ENTERPRISE
      ]
    }
  },
  [MessageSubType.INTERVIEW_SCHEDULE_RESCHEDULED]: {
    biz: {
      segments: [
        { kind: 'template', templateKey: 'notify.messageTemplates.interviewScheduleRescheduled', boldFields: ['candidateName'] },
        { kind: 'boldText', i18nKey: 'notify.messageTemplates.rescheduled' },
        { kind: 'text', i18nKey: 'notify.messageTemplates.awaitingConfirm' },
        ...FOOTER_SOURCE
      ]
    },
    agent: {
      segments: [
        {
          kind: 'template',
          templateKey: 'notify.messageTemplates.agent.interviewScheduleRescheduled',
          boldFields: ['candidateName']
        },
        { kind: 'boldText', i18nKey: 'notify.messageTemplates.rescheduled' },
        { kind: 'text', i18nKey: 'notify.messageTemplates.awaitingConfirm' },
        ...FOOTER_ENTERPRISE
      ]
    }
  },
  [MessageSubType.EXPECTED_JOIN_DATE_ADJUSTED]: {
    biz: {
      segments: [
        { kind: 'template', templateKey: 'notify.messageTemplates.confirmJoinDate', boldFields: ['candidateName', 'joinDate'] },
        ...FOOTER_SOURCE
      ]
    },
    agent: {
      segments: [
        { kind: 'template', templateKey: 'notify.messageTemplates.agent.confirmJoinDate', boldFields: ['candidateName', 'joinDate'] },
        ...FOOTER_ENTERPRISE
      ]
    }
  },
  [MessageSubType.CONFIRM_JOIN]: {
    biz: {
      segments: [
        { kind: 'template', templateKey: 'notify.messageTemplates.confirmJoin', boldFields: ['candidateName'] },
        { kind: 'boldText', i18nKey: 'notify.messageTemplates.confirmedJoin' },
        ...FOOTER_SOURCE
      ]
    },
    agent: {
      segments: [
        { kind: 'template', templateKey: 'notify.messageTemplates.agent.confirmJoin', boldFields: ['candidateName'] },
        { kind: 'boldText', i18nKey: 'notify.messageTemplates.confirmedJoin' },
        ...FOOTER_ENTERPRISE
      ]
    }
  },
  [MessageSubType.SELECTION_TERMINATE]: {
    biz: {
      segments: [
        { kind: 'template', templateKey: 'notify.messageTemplates.selectionTerminate', boldFields: ['candidateName', 'stepName'] },
        // { kind: 'text', i18nKey: 'notify.messageTemplates.terminateStage' },
        // { kind: 'text', i18nKey: 'notify.messageTemplates.stage' },
        { kind: 'break'},
        { kind: 'labelValue', labelKey: 'notify.messageTemplates.terminateReason', field: 'terminationReason', fallback: '-' },
        ...FOOTER_SOURCE
      ]
    },
    agent: {
      segments: [
        {
          kind: 'template',
          templateKey: 'notify.messageTemplates.agent.selectionTerminate',
          boldFields: ['candidateName', 'stepName']
        },
        { kind: 'break'},
        { kind: 'labelValue', labelKey: 'notify.messageTemplates.terminateReason', field: 'terminationReason', fallback: '-' },
        ...FOOTER_ENTERPRISE
      ]
    }
  },
  [MessageSubType.INTERNAL_COMMENT]: {
    biz: {
      segments: [
        {
          kind: 'template',
          templateKey: 'notify.messageTemplates.internalComment',
          boldFields: ['userName', 'candidateName', 'remarkContent'],
          fieldFallbacks: { remarkContent: '-' }
        },
        ...FOOTER_SOURCE
      ]
    },
    agent: {
      segments: [
        {
          kind: 'template',
          templateKey: 'notify.messageTemplates.agent.internalComment',
          boldFields: ['userName', 'candidateName', 'remarkContent'],
          fieldFallbacks: { remarkContent: '-' }
        },
        ...FOOTER_ENTERPRISE
      ]
    }
  },
  [MessageSubType.EXTERNAL_CONTACT]: {
    biz: {
      segments: [
        {
          kind: 'template',
          templateKey: 'notify.messageTemplates.externalContact',
          boldFields: ['userName', 'candidateName', 'remarkContent'],
          fieldFallbacks: { remarkContent: '-' }
        },
        ...FOOTER_SOURCE
      ]
    },
    agent: {
      segments: [
        {
          kind: 'template',
          templateKey: 'notify.messageTemplates.agent.externalContact',
          boldFields: ['userName', 'candidateName', 'remarkContent'],
          fieldFallbacks: { remarkContent: '-' }
        },
        ...FOOTER_ENTERPRISE
      ]
    }
  },
  [MessageSubType.OFFER_LETTER_SENT]: {
    biz: {
      segments: [
        { kind: 'template', templateKey: 'notify.messageTemplates.offerNoticeSent', boldFields: ['candidateName', 'joinDate'] },
        ...FOOTER_SOURCE
      ]
    },
    agent: {
      segments: [
        { kind: 'template', templateKey: 'notify.messageTemplates.offerNoticeSent', boldFields: ['candidateName', 'joinDate'] },
        ...FOOTER_ENTERPRISE
      ]
    }
  },
  [MessageSubType.CANDIDATE_REGISTER]: {
    biz: {
      segments: [
        { kind: 'template', templateKey: 'notify.messageTemplates.candidateRegister', boldFields: ['candidateName'] },
        { kind: 'break' },
        { kind: 'labelValue', labelKey: 'notify.messageTemplates.candidateChannel', field: 'sourceField' }
      ]
    },
    agent: {
      segments: [
        { kind: 'template', templateKey: 'notify.messageTemplates.agent.candidateRegister', boldFields: ['candidateName'] },
        { kind: 'break' },
        { kind: 'labelValue', labelKey: 'notify.messageTemplates.candidateChannel', field: 'sourceField' }
      ]
    }
  }
}

/**
 * 根据消息子类型和端（企业端/猎头端）获取对应的渲染配置
 * @param subType 消息子类型
 * @param isAgent 是否猎头端（productId === 3）
 */
export function getMessageSideConfig(subType: MessageSubType, isAgent: boolean): MessageSideConfig | undefined {
  const config = MessageRenderConfigs[subType]
  if (!config) return undefined
  return (isAgent ? config.agent : config.biz) ?? config.biz
}

export enum DefaultVirRole {
  candidateOwner = 101,
  selectionOwner = 102,
}
/**
 * 消息类型枚举
 */
export enum MessageType {
  /** 通知 */
  NOTIFICATION = 1,
  /** 公告 */
  ANNOUNCEMENT = 2
}

/**
 * 消息分类枚举
 */
export enum MessageCategory {
  /** 职位 */
  JOB = 1,
  /** 选考 */
  SELECTION = 2
}
